/**
 * Cross-package runtime contracts (ARCHITECTURE §6, §8):
 *   - RollbackGame<S, I, E, C>  the deterministic game as seen by netcode (implemented by `@hf/sim`)
 *   - Transport                 an unreliable datagram pipe (WebRTC / WS relay / loopback, implemented by `@hf/netcode`)
 *   - BinaryChannel             a reliable binary side-channel (the lobby WebSocket in relay mode) that the client
 *                               hands to `@hf/netcode`'s createRelayTransport
 *   - MatchDriver<S, E>         what the client game loop drives each tick (local sim driver in the client,
 *                               RollbackSession in `@hf/netcode`)
 */
import type { InputFrame } from './input';

// ── Rollback game ──────────────────────────────────────────────────────────────────────────────────────

/** Anything events can be pushed into (a plain array works). */
export interface EventSink<E> {
  push(event: E): unknown;
}

/**
 * The deterministic simulation contract. All methods must be pure functions of their arguments (no clocks, no
 * Math.random, no globals). `step` MUTATES `state` in place and must not allocate in steady state.
 */
export interface RollbackGame<S, I = InputFrame, E = unknown, C = unknown> {
  /** Number of players (inputs per tick). */
  readonly playerCount: number;
  /** The input assumed for a player before any is known (and as the base of prediction). */
  readonly neutralInput: I;
  /** Create a brand-new state. Only call outside the hot loop. */
  createState(config: C): S;
  /**
   * Advance exactly one tick. `inputs[p]` is player p's input for this tick. Events produced during the tick are
   * pushed into `events`.
   *
   * ALWAYS collect events when resimulating after a rollback: the resimulated ticks are exactly the ones whose
   * earlier events came from a wrong prediction (a TOUCHDOWN or INTERCEPTION may exist only after the correction).
   * The session de-duplicates them by `eventKey` against the events already delivered for that tick. Pass null
   * ONLY for headless runs that nobody watches: sync-test, soak tests, determinism tests, AI lookahead, replay
   * fast-forward to a seek point.
   */
  step(state: S, inputs: readonly I[], events: EventSink<E> | null): void;
  /** Copy `src` into `dst` without allocating (both created by createState with the same config). */
  cloneInto(dst: S, src: S): void;
  /** Allocate a deep copy (snapshot ring buffers are pre-filled with these). */
  clone(src: S): S;
  /** 32-bit hash over every field (desync detection). */
  hash(state: S): number;
  /** The tick number stored in the state (number of steps taken since createState). */
  tickOf(state: S): number;
}

// ── Transport ──────────────────────────────────────────────────────────────────────────────────────────

export type TransportKind = 'webrtc' | 'relay' | 'loopback';
export type TransportState = 'connecting' | 'open' | 'closed';

export interface TransportStats {
  /** Smoothed round-trip time, ms (0 until measured). */
  rttMs: number;
  /** RTT mean deviation, ms. */
  rttVarMs: number;
  /** Last raw RTT sample, ms. */
  lastRttMs: number;
  packetsSent: number;
  packetsReceived: number;
  bytesSent: number;
  bytesReceived: number;
  /** Estimated loss 0..1 if the transport can tell (else 0). */
  loss: number;
}

/**
 * An unreliable, unordered, message-oriented pipe between exactly two peers. `send` never throws for a closed
 * transport (it drops). Handlers are subscribed with `on*` and unsubscribed with the returned function.
 */
export interface Transport {
  readonly kind: TransportKind;
  readonly state: TransportState;
  send(data: Uint8Array): void;
  onMessage(handler: (data: Uint8Array) => void): () => void;
  onStateChange(handler: (state: TransportState) => void): () => void;
  /** Live stats object (mutated in place by the transport and by `RttEstimator` users). */
  readonly stats: TransportStats;
  /** Feed an RTT sample measured by the session's PING/PONG (updates `stats`). */
  reportRtt(sampleMs: number): void;
  close(): void;
}

export const createTransportStats = (): TransportStats => ({
  rttMs: 0,
  rttVarMs: 0,
  lastRttMs: 0,
  packetsSent: 0,
  packetsReceived: 0,
  bytesSent: 0,
  bytesReceived: 0,
  loss: 0,
});

/** RFC 6298-style smoothed RTT estimator. Writes into a TransportStats. */
export class RttEstimator {
  private initialized = false;
  constructor(
    readonly stats: TransportStats = createTransportStats(),
    private readonly alpha = 1 / 8,
    private readonly beta = 1 / 4,
  ) {}
  sample(rtt: number): void {
    if (!(rtt >= 0) || !Number.isFinite(rtt)) return;
    const s = this.stats;
    s.lastRttMs = rtt;
    if (!this.initialized) {
      s.rttMs = rtt;
      s.rttVarMs = rtt / 2;
      this.initialized = true;
      return;
    }
    s.rttVarMs = (1 - this.beta) * s.rttVarMs + this.beta * Math.abs(s.rttMs - rtt);
    s.rttMs = (1 - this.alpha) * s.rttMs + this.alpha * rtt;
  }
}

// ── Binary channel (client-owned socket → netcode relay transport) ────────────────────────────────────────

/**
 * A binary message channel owned by someone else (the client's `OnlineClient` lobby WebSocket). `@hf/netcode`'s
 * `createRelayTransport(channel)` wraps it into a `Transport`. Only BINARY frames go through it; JSON lobby
 * messages stay with the owner. Closing the transport must NOT close the channel (the owner does that).
 */
export interface BinaryChannel {
  readonly open: boolean;
  send(data: Uint8Array): void;
  onBinary(handler: (data: Uint8Array) => void): () => void;
  onClose(handler: () => void): () => void;
}

// ── Match driver (client game loop ↔ local sim / rollback session) ─────────────────────────────────────

export type DriverStatus = 'waiting' | 'running' | 'stalled' | 'desynced' | 'disconnected' | 'ended';

export interface NetStats {
  rttMs: number;
  jitterMs: number;
  inputDelay: number;
  /** Ticks resimulated by the last rollback. */
  lastRollback: number;
  maxRollback: number;
  /** Time-sync advantage (localAdv − remoteAdv) / 2, ticks (positive = we are ahead and will wait). */
  frameAdvantage: number;
  /** Remote peer's input delay (from its INPUT header). */
  remoteInputDelay: number;
  /** Ticks stalled waiting for the remote. */
  stalls: number;
  transport: TransportKind;
  /** Last confirmed tick (both inputs known). */
  confirmedTick: number;
}

/**
 * One match in progress, as seen by the client's fixed-step loop. The loop calls `tick()` once per 60 Hz step with
 * the inputs of the LOCAL players (2 for local versus/CPU-as-input, 1 for online), then renders by interpolating
 * `previous` → `current`.
 */
export interface MatchDriver<S, E> {
  readonly status: DriverStatus;
  /**
   * How the client's fixed-step loop treats a long frame (tab hidden, debugger, GC):
   *   'drop'     offline: drop the excess time (the match just pauses).
   *   'catch_up' online: never drop ticks; run the backlog over the next frames (at most the loop's
   *              maxCatchUpTicks per frame) so the remote peer is not left stalling.
   */
  readonly timePolicy: 'drop' | 'catch_up';
  /** Latest state to display (may be predicted online). Do not mutate. */
  readonly current: S;
  /** The state one tick before `current` (for render interpolation). Do not mutate. */
  readonly previous: S;
  /**
   * Advance by one tick. Returns false if the driver stalled (time sync / waiting for remote): the loop should
   * still render, and should not treat the tick as consumed for hit-stop purposes.
   */
  tick(localInputs: readonly InputFrame[]): boolean;
  /**
   * Move events produced since the last drain into `out`. Online, events are collected on EVERY simulated tick,
   * resimulation included, and de-duplicated by `eventKey` (tick, type, actor) against events already delivered,
   * so an event that only exists after a correction (e.g. a TOUCHDOWN on the confirmed timeline) is delivered
   * exactly once; events of rolled-back predictions that did not happen again are NOT retracted (renderers keep
   * effects short).
   */
  drainEvents(out: E[]): void;
  /**
   * Subscribe to CONFIRMED ticks (both players' inputs final, never rolled back again), in tick order, each once.
   * `inputs[p]` is player p's input of tick `tick` (the input that advanced the state from `tick` to `tick + 1`).
   * LocalDriver fires it every tick. Replays / highlight recorders use this, never `current` (which may be a
   * prediction online): replay = (snapshot at some confirmed tick) + confirmed inputs, resimulated into a
   * separate state with `RollbackGame.step` (events on while the replay is shown). `inputs` is reused: copy it if you keep it.
   */
  onConfirmed(handler: (tick: number, inputs: readonly InputFrame[]) => void): () => void;
  /** Online drivers expose live network stats. */
  netStats(): NetStats | null;
  dispose(): void;
}
